<template>
  <header class="mobile-header">
    <div class="header-logo" @click="goToDomains">
      <img alt="Segofy logo" src="../assets/logo.png" class="logo">
    </div>

    <button class="burger-button" @click="toggleMenu" aria-label="Toggle menu">
      <span class="burger-line" :class="{ 'open': isOpen }"></span>
      <span class="burger-line" :class="{ 'open': isOpen }"></span>
      <span class="burger-line" :class="{ 'open': isOpen }"></span>
    </button>

    <div class="burger-overlay" :class="{ 'open': isOpen }" @click="closeMenu"></div>

    <nav class="burger-nav" :class="{ 'open': isOpen }">
      <div class="burger-nav-items">
        <div class="nav-item" :class="{ active: isActive('/domains') }" @click="goToDomains">
          <img src="../assets/menu-sites.png" alt="Sites" class="nav-icon">
          <span class="nav-text">Sites</span>
        </div>
        <div class="nav-item" :class="{ active: isActive('/profile') }" @click="goToAccount">
          <img src="../assets/menu-account.png" alt="Account" class="nav-icon">
          <span class="nav-text">Account</span>
        </div>
        <div v-if="subscriptionsEnabled" class="nav-item" :class="{ active: isActive('/subscriptions') }" @click="goToSubscriptions">
          <img src="../assets/menu-account.png" alt="Subscriptions" class="nav-icon">
          <span class="nav-text">Subscriptions</span>
        </div>
        <div v-if="subscriptionsEnabled" class="nav-item" :class="{ active: isActive('/payments') }" @click="goToPayments">
          <img src="../assets/menu-account.png" alt="Payments" class="nav-icon">
          <span class="nav-text">Payments</span>
        </div>
        <div v-if="isAdmin" class="admin-group">
          <div class="group-header">
            <img src="../assets/menu-account.png" alt="Admin" class="nav-icon">
            <span class="nav-text">Admin</span>
          </div>
          <div class="submenu">
            <div class="submenu-item" :class="{ active: isActive('/admin/sites') }" @click="goToUsers">
              <span class="submenu-text">Sites</span>
            </div>
            <div class="submenu-item" :class="{ expanded: subscriptionsExpanded }">
              <span class="submenu-text" @click="toggleSubscriptions">Subscriptions</span>
              <div v-if="subscriptionsExpanded" class="nested-submenu">
                <div class="nested-submenu-item" :class="{ active: isActive('/admin/subscription-plans') }" @click="goToPlans">
                  <span class="nested-submenu-text">Plans</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="burger-footer">
        <span class="version">API v{{ version }}</span>
      </div>
    </nav>
  </header>
</template>

<script>
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, watch } from 'vue'
import { api, getCurrentUser } from '../utils/api'

export default {
  name: 'MobileHeader',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const version = ref('')
    const isOpen = ref(false)
    const user = ref(null)
    const isAdmin = ref(false)
    const subscriptionsExpanded = ref(false)
    const envValue = process.env.VUE_APP_FEATURE_FLAG_SUBSCRIPTIONS_ENABLE
    console.log('VUE_APP_FEATURE_FLAG_SUBSCRIPTIONS_ENABLE:', envValue, 'Type:', typeof envValue)
    // Check if enabled - handle string 'true', boolean true, or 'True'/'TRUE'
    const isEnabled = envValue === 'true' || envValue === true || String(envValue).toLowerCase() === 'true'
    console.log('Subscriptions enabled:', isEnabled)
    const subscriptionsEnabled = ref(isEnabled)

    const fetchVersion = async () => {
      try {
        const data = await api('/version')
        version.value = data.version
      } catch (error) {
        console.error('Failed to fetch API version:', error)
        version.value = 'unknown'
      }
    }

    const fetchUser = async () => {
      // Don't fetch user data on login/register pages
      if (route.path === '/login' || route.path === '/register') {
        return
      }

      // Don't fetch user data if there's no token
      const token = (() => { try { return localStorage.getItem('fic') } catch (_) { return null } })()
      if (!token) {
        return
      }

      try {
        const userData = await getCurrentUser(router)
        user.value = userData
        isAdmin.value = userData?.isAdmin === true
      } catch (error) {
        console.error('Failed to fetch user data:', error)
        user.value = null
        isAdmin.value = false
      }
    }

    const toggleMenu = () => {
      isOpen.value = !isOpen.value
    }

    const closeMenu = () => {
      isOpen.value = false
    }

    const goToDomains = () => {
      router.push('/domains')
      closeMenu()
    }

    const goToAccount = () => {
      router.push('/profile')
      closeMenu()
    }

    const goToSubscriptions = () => {
      router.push('/subscriptions')
      closeMenu()
    }

    const goToPayments = () => {
      router.push('/payments')
      closeMenu()
    }

    const goToUsers = () => {
      router.push('/admin/sites')
      closeMenu()
    }

    const goToPlans = () => {
      router.push('/admin/subscription-plans')
      closeMenu()
    }

    const toggleSubscriptions = () => {
      subscriptionsExpanded.value = !subscriptionsExpanded.value
    }

    const isActive = (path) => {
      return route.path === path || route.path.startsWith(path + '/')
    }

    // Auto-expand subscriptions if on plans page
    watch(() => route.path, (path) => {
      if (path.startsWith('/admin/subscription-plans')) {
        subscriptionsExpanded.value = true
      }
    }, { immediate: true })

    onMounted(() => {
      fetchVersion()
      fetchUser()
      // Auto-expand subscriptions if on plans page
      if (route.path.startsWith('/admin/subscription-plans')) {
        subscriptionsExpanded.value = true
      }
    })

    return {
      version,
      isOpen,
      user,
      isAdmin,
      subscriptionsExpanded,
      subscriptionsEnabled,
      toggleMenu,
      closeMenu,
      goToDomains,
      goToAccount,
      goToSubscriptions,
      goToPayments,
      goToUsers,
      goToPlans,
      toggleSubscriptions,
      isActive
    }
  }
}
</script>

<style scoped>
.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 1001;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-logo {
  cursor: pointer;
}

.logo {
  height: 32px;
  transition: transform 0.2s;
}

.logo:hover {
  transform: scale(1.05);
}

.burger-button {
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  z-index: 1002;
  transition: background-color 0.3s ease;
  border-radius: 6px;
}

.burger-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.burger-line {
  width: 20px;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  transform-origin: center;
}

.burger-line.open:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.burger-line.open:nth-child(2) {
  opacity: 0;
}

.burger-line.open:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -6px);
}

.burger-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 999;
}

.burger-overlay.open {
  opacity: 1;
  visibility: visible;
}

.burger-nav {
  position: fixed;
  top: 60px;
  right: -300px;
  width: 280px;
  height: calc(100vh - 60px);
  background-color: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  transition: right 0.3s ease;
  z-index: 1000;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.15);
}

.burger-nav.open {
  right: 0;
}

.burger-nav-items {
  flex: 1;
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: rgba(255, 255, 255, 0.8);
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-right: 3px solid var(--accent-color, #007bff);
}

.nav-icon {
  margin-right: 12px;
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.nav-text {
  font-weight: 500;
  font-size: 16px;
}

.burger-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.version {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  opacity: 0.7;
}

.admin-group {
  display: flex;
  flex-direction: column;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  background-color: rgba(255, 255, 255, 0.05);
}

.submenu {
  background-color: rgba(0, 0, 0, 0.2);
}

.submenu-item {
  padding: 12px 20px 12px 60px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: rgba(255, 255, 255, 0.7);
}

.submenu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.submenu-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-right: 3px solid var(--accent-color, #007bff);
}

.submenu-text {
  font-weight: 400;
  font-size: 14px;
}

.submenu-item.expanded {
  background-color: rgba(255, 255, 255, 0.05);
}

.nested-submenu {
  background-color: rgba(0, 0, 0, 0.3);
  margin-top: 4px;
}

.nested-submenu-item {
  padding: 10px 20px 10px 80px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: rgba(255, 255, 255, 0.6);
}

.nested-submenu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nested-submenu-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-right: 3px solid var(--accent-color, #007bff);
}

.nested-submenu-text {
  font-weight: 400;
  font-size: 13px;
}
</style>